<template>

  <v-container class="page page-accept-member-invite">

    <v-card flat class="pt-4 d-flex align-center justify-center registration-form-area">
      <!------------------------->
      <!-- Invite data loading -->
      <!------------------------->
      <template v-if="isDataLoading">
        <v-container class="d-flex flex-column align-center justify-center">
          <v-progress-circular indeterminate :size="50" style="margin-top:30px;" color="primary" />
        </v-container>
      </template>

      <!------------------------------->
      <!-- Invite data loading error -->
      <!------------------------------->
      <template v-else-if="dataLoadingError">
        <v-icon class="mr-2" color="#cc0000">mdi-alert-circle-outline</v-icon>
        <span class="error-message-text">{{ dataLoadingError }}</span>
      </template>

      <!------------------------------>
      <!-- Already accepted message -->
      <!------------------------------>
      <template v-else-if="isAlreadyAccepted">
        <div class="confirmation-message-area">
          <div class="main-text">This invite has already been accepted.</div>
        </div>
      </template>

      <!----------------------------->
      <!-- Invite canceled message -->
      <!----------------------------->
      <template v-else-if="isCanceled">
        <div class="confirmation-message-area">
          <div class="main-text">This invite is no longer available.</div>
        </div>
      </template>

      <!---------------------------------->
      <!-- Accepted invite confirmation -->
      <!---------------------------------->
      <template v-else-if="registeredUser">
        <div class="confirmation-message-area">
          <div class="main-text">You have accepted the invite for {{ orgName }}!</div>
          <div class="mt-6 confirmation-message-text">
            <div>An email has been sent to:</div>
            <div style="color:#ffffff;font-weight:500;">{{ registeredEmail }}</div>
          </div>
          <v-row align="center" class="mt-8 highlighted-info-container">
            <v-col cols="12" class="d-flex align-center justify-center">
              <v-icon style="font-size:1.6rem;" color="yellow darken-3" class="pr-3">mdi-information-outline</v-icon>
              <div class="highlighted-info-text">You must confirm your email address to login.</div>
            </v-col>
          </v-row>
        </div>
      </template>

      <!------------------------>
      <!-- Accept invite form -->
      <!------------------------>
      <v-form class="registration-form" autocomplete="off" v-else>
        <!-- title -->
        <v-row>
          <v-col align="center" class="form-title">
            {{ formTitleText }}
          </v-col>
        </v-row>

        <!-- information area -->
        <v-row class="mt-0">
          <v-col align="center" class="main-text">
            <div>You have been invited to join {{ orgName }} by {{ inviterName }}</div>
          </v-col>
        </v-row>
        <v-row class="mt-5">
          <v-col align="center" class="info-text">
            <div>Please provide your name and create a password.</div>
          </v-col>
        </v-row>

        <!-- first name -->
        <v-row class="mt-4" align="center">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <span>First name:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-text-field
                outlined
                hide-details
                required
                autocomplete="first-name"
                v-model.trim="firstName" />
          </v-col>
        </v-row>

        <!-- last name -->
        <v-row class="mt-5" align="center">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <span>Last name:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-text-field
                outlined
                hide-details
                required
                autocomplete="last-name"
                v-model.trim="lastName" />
          </v-col>
        </v-row>

        <v-row class="mt-5" align="start">
          <v-col cols="12" xs="12" sm="3" md="3" lg="3">
            <div class="pt-4">Password:</div>
          </v-col>
          <v-col cols="12" xs="12" sm="7" md="7" lg="7">
            <v-row>
              <v-col class="mb-0 pb-0">
                <v-text-field
                    outlined
                    required
                    v-model.trim="password"
                    autocomplete="new-password"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="showPassword ? 'text' : 'password'"
                    @click:append="showPassword = !showPassword" />
              </v-col>
            </v-row>
            <!-- password requirements -->
            <v-row class="pa-0 ma-0 mb-10">
              <v-col class="pa-0 ma-0">
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="password-requirements-text">
                      Password requirements
                      <v-icon style="font-size:17px;" color="#34b3db" class="pl-1">mdi-information-outline</v-icon>
                    </div>
                  </template>
                  <div>
                    <span>Must be at least 8 characters, and contain:</span><br />
                    <ul class="mt-1">
                      <li>1 number</li>
                      <li>1 uppercase letter</li>
                      <li>1 lowercase letter</li>
                      <li>1 special character</li>
                    </ul>
                  </div>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Accept terms of use -->
        <v-row class="mt-1 mb-4" align="center">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-checkbox
                label="I agree to the terms"
                v-model="acceptTerms" />
            <div class="ml-5"><a @click.prevent="isTermsDialogOpen = true" href="#">Terms of Use</a></div>
          </v-col>
        </v-row>

        <!-- Registration error display -->
        <template v-if="processingError">
          <transition name="fade" appear>
            <v-row class="mt-0 mb-1">
              <v-col class="d-flex align-center justify-center">
                <v-icon class="mr-2" color="#cc0000">mdi-alert-circle-outline</v-icon>
                <span class="error-message-text">{{ processingError }}</span>
                <v-btn class="ml-3" @click="clearError">OK</v-btn>
              </v-col>
            </v-row>
          </transition>
        </template>

        <!-- Accept invite button -->
        <v-row class="mt-0">
          <v-col class="d-flex align-center justify-center">
            <v-progress-circular indeterminate :size="40" color="primary" style="margin-top:12px;" v-if="isProcessing" />
            <v-btn
                v-else
                xlarge
                :disabled="!canAccept"
                color="primary"
                height="54"
                @click="acceptInvite">
              Accept Invite
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!------------------------->
    <!-- Terms of Use dialog -->
    <!------------------------->
    <v-dialog persistent fullscreen v-model="isTermsDialogOpen" align="center">
      <v-card align="center" style="background-color:#080404;">
        <route-leaf-emblem style="margin-top:32px;margin-right:10px;" />
        <route-leaf-logo :linkTo="null" style="padding-bottom:14px;" />
        <terms-of-use-page :showBackToApp="false" />
        <v-card-actions class="mt-5">
          <v-btn
              color="primary"
              class="px-5"
              @click="isTermsDialogOpen = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import RouteLeafEmblem from '@/components/app/RouteLeafEmblem'
import RouteLeafLogo from '@/components/app/RouteLeafLogo'
import TermsOfUsePage from '@/pages/public/TermsOfUsePage'

export default {
  name: 'AcceptMemberInvitePage',

  components: {
    RouteLeafEmblem,
    RouteLeafLogo,
    TermsOfUsePage
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      inviteData: null,
      isProcessing: false,
      processingError: null,
      showPassword: false,
      registeredUser: null,
      // --------------- Input fields
      firstName: null,
      lastName: null,
      password: null,
      acceptTerms: false,
      // --------------- Terms dialog
      isTermsDialogOpen: false
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    inviteToken () {
      return this.$route.query?.invite_token
    },
    formTitleText () {
      return 'Accept Invite'
    },
    isAlreadyAccepted () {
      return (this.inviteData)
        ? this.inviteData.status === 'accepted'
        : false
    },
    isCanceled () {
      return (this.inviteData)
        ? this.inviteData.status === 'canceled'
        : false
    },
    canAccept () {
      return (
        this.inviteToken &&
        this.firstName &&
        this.lastName &&
        this.password &&
        this.acceptTerms
      )
    },
    orgName () {
      return (this.inviteData)
        ? this.inviteData?.org?.name
        : this.appName
    },
    inviterName () {
      return (this.inviteData && this.inviteData.inviter)
        ? `${this.inviteData.inviter.first_name} ${this.inviteData.inviter.last_name}`
        : ''
    },
    existingUser () {
      return (this.inviteData)
        ? this.inviteData.user
        : null
    },
    registeredEmail () {
      return this.registeredUser.email
    }
  },

  async beforeMount () {
    await this.loadInvite()
  },

  beforeDestroy () {
    this.resetForm()
    this.inviteData = null
  },

  methods: {
    // Reset form
    resetForm () {
      this.dataLoadingError = null
      this.processingError = null
      this.registeredUser = null
      this.firstName = null
      this.lastName = null
      this.password = null
    },

    // Clear active error
    clearError () {
      this.processingError = null
    },

    // Load invite
    async loadInvite () {
      if (!this.inviteToken) {
        this.dataLoadingError = 'This is a restricted page.'
        return false
      }

      this.isDataLoading = true

      try {
        const uri = 'org/invite'
        const params = {
          invite_token: this.inviteToken
        }
        const inviteDataPayload = await this.$root.dataApi.get(uri, { params })
        this.inviteData = this.$root.toModel(inviteDataPayload)
      } catch (err) {
        if (err.status === 404) this.dataLoadingError = 'The requested invite is not valid.'
        else this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    },

    // Perform accept invite
    async acceptInvite () {
      if (!this.canAccept) {
        this.processingError = 'You must provide all fields to accept the invite.'
        return false
      }

      this.isProcessing = true

      try {
        const uri = 'org/accept-invite-new-user'
        const bodyData = {
          invite_token: this.inviteToken,
          first_name: this.firstName,
          last_name: this.lastName,
          password: this.password
        }
        const registeredUserPayload = await this.$root.dataApi.post(uri, { bodyData })
        this.registeredUser = this.$root.toModel(registeredUserPayload)
      } catch (err) {
        // Handle error for user display
        let message = null
        if (err.code) {
          switch (err.code) {
            case 'InvalidPasswordException': message = 'Invalid password provided'; break
            default: message = 'There was an issue accepting the invite'
          }
        } else {
          message = err.message || err.text || err
        }
        this.processingError = message
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style scoped>
  .page-accept-member-invite {
    margin-top: 60px;
  }

  .registration-form-area {
    padding: 24px 24px;
    background-color: #080404 !important;
  }

  .registration-form {
    min-width: 300px;
  }

  .form-title {
    font-size: 1.5rem;
    color: #F8A824;
    font-weight: 400;
  }

  .main-text {
    font-size: 1.2rem;
    color: #9c9c9c;
    font-weight: 400;
  }

  .info-text {
    font-size: 1.08rem;
    font-weight: 400;
  }

  .password-requirements-text {
    cursor: pointer;
    font-size: 1.0rem;
    color: #34b3db;
  }

  .error-message-text {
    font-size: 1.08rem;
    color: #cc0000;
    font-weight: 400;
  }

  .confirmation-message-area {
    text-align: center;
    max-width: 570px;
  }

  .confirmation-message-text {
    font-size: 19px;
    color: #9c9c9c;
    font-weight: 400;
  }

  .highlighted-info-container {
    border: 1px solid #F9A825;
    border-radius: 6px;
  }

  .highlighted-info-text {
    font-size: 1.08rem;
    color: #F9A825;
    font-weight: 400;
  }

  .page-terms-of-use {
    margin-top: 10px;
  }
</style>
